import React, { useState, useRef, useCallback } from "react";
import Avatar from "../../components/Common/Avatar";
import Button from "../../components/Buttons/Button";
import { dateFormator } from "../../utils";
import DropdownMenu from "../Buttons/DropdownMenu";
import Modal from "../Modal/Modal";
import {useOutsideClick} from "../../utils/useOutsideClick";

const ChatBubble = ({
  data,
  sender,
  loginUserId,
  onDelete,
  onReply,
  QUICK_EMOJIS,
  showActions,
  onReact,
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const dropdownRef = useRef(null);
  const emojiPickerRef = useRef(null);

  const isOwn = loginUserId === sender?.id;
  const reactions = data?.reactions || {};

  // optimized outside click
  useOutsideClick([dropdownRef, emojiPickerRef], () => {
    setIsDropdownOpen(false);
    setShowEmojiPicker(false);
  });

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(data.message);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      setIsDropdownOpen(false);
    }, 1500);
  }, [data.message]);

  const handleReply = useCallback(() => {
    onReply?.(data, sender);
    setIsDropdownOpen(false);
    setShowEmojiPicker(false);
  }, [data, sender, onReply]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
    setIsDropdownOpen(false);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    onDelete?.(data.id);
    setShowDeleteConfirm(false);
  }, [data.id, onDelete]);

  const handleReact = useCallback(
    (emoji) => {
      onReact?.(data.id, emoji);
    },
    [data.id, onReact]
  );

  const dropdownMenuItems = (
    <>
      {showEmojiPicker && (
        <div
          ref={emojiPickerRef}
          className="px-2 pb-2 pt-1"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex gap-1 bg-gray-50 border border-gray-200 rounded-xl p-1.5">
            {QUICK_EMOJIS?.map((emoji) => {
              const isReacted = reactions[emoji]?.reacted;
              return (
                <Button
                  key={emoji}
                  icon={emoji}
                  variant="text"
                  color="info"
                  className={`hover:scale-125 transition-all p-1! rounded-lg ${
                    isReacted
                      ? "bg-info-100 ring-1 ring-info-300"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReact(emoji);
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-col min-w-[160px]">
        <Button
          title={copied ? "Copied!" : "Copy"}
          icon={"content_copy"}
          iconPosition="before"
          color={"black"}
          variant="text"
          onClick={handleCopy}
          className="justify-start"
        />

        <Button
          title="Reply"
          icon="reply"
          iconPosition="before"
          color="black"
          variant="text"
          onClick={handleReply}
          className="justify-start"
        />

        <Button
          title="React"
          icon="mood"
          iconPosition="before"
          color="black"
          variant="text"
          onClick={(e) => {
            e.stopPropagation();
            setShowEmojiPicker((p) => !p);
          }}
          className="justify-start w-full"
        />

        {isOwn && (
          <Button
            title={"Delete"}
            icon={"delete"}
            iconPosition="before"
            color="black"
            variant="text"
            onClick={handleDeleteClick}
            className="justify-start"
          />
        )}
      </div>
    </>
  );

  return (
    <>
      <div
        className={`flex ${isOwn ? "justify-end" : ""}`}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          if (!isDropdownOpen && !showEmojiPicker) setIsHovering(false);
        }}
      >
        <div className="w-full max-w-xl flex flex-col gap-2 my-5">
          <div
            className={`flex justify-between items-center ${
              isOwn ? "flex-row-reverse" : ""
            }`}
          >
            <div
              className={`flex items-center gap-2 ${
                isOwn ? "flex-row-reverse" : ""
              }`}
            >
              <Avatar
                size="sm"
                alt={sender?.name}
                src={sender?.profilePhotoUrl}
              />
              <div className="font-semibold">{sender?.name}</div>
              <div className="text-xs text-gray-400">
                {dateFormator(data.timestamp, "time-ampm")}
              </div>
            </div>

            {showActions && (isHovering || isDropdownOpen) && (
              <div ref={dropdownRef}>
                <DropdownMenu
                  MenuSection={dropdownMenuItems}
                  isOpen={isDropdownOpen}
                  onToggle={() => setIsDropdownOpen((p) => !p)}
                >
                  <Button
                    type="icon"
                    variant="text"
                    icon="menu"
                    color="black"
                    size="xs"
                  />
                </DropdownMenu>
              </div>
            )}
          </div>

          {data?.replyTo && (
            <div className="flex items-start gap-2 bg-info-50 border-l-2 border-info-400 rounded px-3 py-1.5 mb-1">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-info-600">
                  {data?.replyTo?.senderName}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {data?.replyTo?.message}
                </p>
              </div>
            </div>
          )}

          <div className="border border-gray-200 p-3 rounded-md text-sm">
            {data?.message}
          </div>

          {Object.keys(reactions).length > 0 && (
            <div
              className={`flex gap-1 flex-wrap ${
                isOwn ? "justify-end" : ""
              }`}
            >
              {Object.entries(reactions).map(([emoji, { reacted }]) => (
                <Button
                  key={emoji}
                  type="icon"
                  variant={reacted ? "filled" : "outlined"}
                  icon={emoji}
                  color="info"
                  onClick={() => handleReact(emoji)}
                  className={`rounded-lg px-5 py-0 pb-1! ${
                    reacted ? "bg-info-100" : "bg-info-50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal
        title={"Delete Message"}
        onClose={() => setShowDeleteConfirm(false)}
        isOpen={showDeleteConfirm}
        width="max-w-lg"
      >
        <p className="text-md text-gray-500 text-center">
          This action cannot be undone.
        </p>
        <div className="flex gap-5 justify-center mt-4">
          <Button
            title={"Cancel"}
            variant="outlined"
            onClick={() => setShowDeleteConfirm(false)}
          />
          <Button title={"Delete"} onClick={handleDeleteConfirm} />
        </div>
      </Modal>
    </>
  );
};

export default React.memo(ChatBubble);